import dayjs from "dayjs";
import type { Customer } from "@/types/general";
import type { AddProductFormSchemeTyping, DiscountOption } from "./addProductDraftShared";
import {
    OrderCurrency,
    roundToCurrency,
    toOrderCurrencyPrice,
} from "@components/Dashboard/pages/Sales/Orders/orderCurrency";

interface BuildDiscountOptionsArguments {
    customer?: Customer.Extended;
    product?: AddProductFormSchemeTyping["product"];
    // Product and customer prices are in the base currency, the options are priced in the order currency
    orderCurrency: OrderCurrency;
    now?: dayjs.Dayjs;
}

type ValidityDates = Pick<DiscountOption, "valid_from" | "valid_to">;

export function isDiscountDateActive(discount: ValidityDates, now: dayjs.Dayjs): boolean {
    const validFrom = discount.valid_from ? dayjs(discount.valid_from) : undefined;
    const validTo = discount.valid_to ? dayjs(discount.valid_to) : undefined;

    return (!validFrom || !now.isBefore(validFrom)) && (!validTo || now.isBefore(validTo));
}

export function isDiscountOptionEligible(
    discount: DiscountOption,
    quantity: number,
    now: dayjs.Dayjs
): boolean {
    const meetsMinimumQuantity = !discount.min_quantity || quantity >= discount.min_quantity;

    return meetsMinimumQuantity && isDiscountDateActive(discount, now);
}

export function selectBestDiscount(
    discounts: DiscountOption[],
    quantity: number,
    now: dayjs.Dayjs
): DiscountOption | undefined {
    return discounts
        .filter((discount) => isDiscountOptionEligible(discount, quantity, now))
        .sort(
            (left, right) =>
                right.discount - left.discount ||
                left.calculated_price - right.calculated_price ||
                left.id.localeCompare(right.id)
        )[0];
}

export function buildDiscountOptions({
    customer,
    product,
    orderCurrency,
    now = dayjs(),
}: BuildDiscountOptionsArguments): DiscountOption[] {
    if (!customer || !product) return [];

    const options: DiscountOption[] = [];
    const sellingPrice = toOrderCurrencyPrice(product.prices.selling_price, orderCurrency);
    const currencyCode = orderCurrency.currency.code;

    const personalProduct = customer.products?.find((entry) => entry.product.id === product.id);
    if (personalProduct && isDiscountDateVisible(personalProduct, now)) {
        options.push(
            createDiscountOption(
                {
                    id: "personal-product",
                    section: "pricesAndDiscounts",
                    source: "personalProduct",
                    discount: personalProduct.discount ?? 0,
                    basePrice: toOrderCurrencyPrice(personalProduct.sale_price, orderCurrency),
                    salePrice: toOrderCurrencyPrice(personalProduct.sale_price, orderCurrency),
                    min_quantity: personalProduct.min_quantity,
                    valid_from: personalProduct.valid_from,
                    valid_to: personalProduct.valid_to,
                },
                currencyCode
            )
        );
    }

    const personalCategory = customer.category_discounts?.find(
        (entry) => entry.category.id === product.category?.id
    );
    if (personalCategory && isDiscountDateVisible(personalCategory, now)) {
        options.push(
            createDiscountOption(
                {
                    id: "personal-category",
                    section: "pricesAndDiscounts",
                    source: "personalCategory",
                    discount: personalCategory.discount ?? 0,
                    basePrice: sellingPrice,
                    min_quantity: personalCategory.min_quantity,
                    valid_from: personalCategory.valid_from,
                    valid_to: personalCategory.valid_to,
                },
                currencyCode
            )
        );
    }

    if (customer.discount !== undefined && customer.discount !== null) {
        options.push(
            createDiscountOption(
                {
                    id: "customer",
                    section: "customer",
                    source: "customer",
                    discount: customer.discount,
                    basePrice: sellingPrice,
                },
                currencyCode
            )
        );
    }

    customer.groups?.forEach((group) => {
        group.products
            ?.filter((entry) => entry.product.id === product.id)
            .forEach((entry, entryIndex) => {
                if (!isDiscountDateVisible(entry, now)) return;

                const salePrice = toOrderCurrencyPrice(entry.sale_price, orderCurrency);
                options.push(
                    createDiscountOption(
                        {
                            id: `group-${group.id}-product-${entry.product.id}-${entryIndex}`,
                            section: "customerGroup",
                            source: "groupProduct",
                            name: group.name,
                            discount: group.discount,
                            basePrice: salePrice,
                            salePrice,
                            valid_from: entry.valid_from,
                            valid_to: entry.valid_to,
                        },
                        currencyCode
                    )
                );
            });

        group.categories
            ?.filter((entry) => entry.category.id === product.category?.id)
            .forEach((entry, entryIndex) => {
                if (!isDiscountDateVisible(entry, now)) return;

                options.push(
                    createDiscountOption(
                        {
                            id: `group-${group.id}-category-${entry.category.id}-${entryIndex}`,
                            section: "customerGroup",
                            source: "groupCategory",
                            name: group.name,
                            discount: group.discount,
                            basePrice: sellingPrice,
                            valid_from: entry.valid_from,
                            valid_to: entry.valid_to,
                        },
                        currencyCode
                    )
                );
            });
    });

    return options;
}

function isDiscountDateVisible(discount: ValidityDates, now: dayjs.Dayjs): boolean {
    if (isDiscountDateActive(discount, now)) return true;
    if (!discount.valid_from) return false;

    const validFrom = dayjs(discount.valid_from);
    return now.isBefore(validFrom) && validFrom.diff(now, "day") <= 30;
}

function createDiscountOption(
    input: Omit<DiscountOption, "calculated_price">,
    currencyCode: string
): DiscountOption {
    return {
        ...input,
        calculated_price: roundToCurrency(
            input.basePrice * (1 - input.discount / 100),
            currencyCode
        ),
    };
}
